import { useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import Button from './Button'
import Spinner from './Spinner'
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline'

// Configure PDF.js worker using CDN to match react-pdf's bundled version
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

/**
 * PDF Viewer Component
 * Displays PDF files with navigation and zoom controls
 * @param {string} signatureToken - Optional signature token for public signing flow
 */
export default function PDFViewer({ file, url, className = '', signatureToken = null }) {
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1.0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setLoading(false)
    setError(null)
  }

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error)
    setError('Failed to load PDF')
    setLoading(false)
  }

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages))
  }

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3.0))
  }

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5))
  }

  // Prepare document options with authentication headers for URL-based PDFs
  const getDocumentOptions = () => {
    if (url) {
      // Get auth token from localStorage or use provided signature token
      const authToken = localStorage.getItem('auth_token')
      const token = signatureToken || authToken
      
      console.log('[PDFViewer DEBUG] URL:', url)
      console.log('[PDFViewer DEBUG] signatureToken prop:', signatureToken)
      console.log('[PDFViewer DEBUG] authToken from localStorage:', authToken)
      console.log('[PDFViewer DEBUG] Using token:', token)
      console.log('[PDFViewer DEBUG] Authorization header:', token ? `Bearer ${token}` : 'None')
      
      return {
        url: url,
        httpHeaders: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      }
    }
    
    // For file objects, return them directly
    return file
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Controls */}
      <div className="flex items-center justify-between gap-4 p-4 bg-gray-100 border-b border-gray-300 rounded-t-lg">
        {/* Page Navigation */}
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1 || loading}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-700 min-w-[80px] text-center">
            {loading ? 'Loading...' : `Page ${pageNumber} of ${numPages}`}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={goToNextPage}
            disabled={pageNumber >= numPages || loading}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5 || loading}
          >
            <MagnifyingGlassMinusIcon className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-700 min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 3.0 || loading}
          >
            <MagnifyingGlassPlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* PDF Document */}
      <div className="flex-1 overflow-auto bg-gray-200 p-4 rounded-b-lg">
        <div className="flex justify-center">
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <Document
              file={getDocumentOptions()}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                className="shadow-lg"
              />
            </Document>
          )}
        </div>
      </div>
    </div>
  )
}